import React from 'react'
import { useUpdate } from '@refinedev/core'
import { App, Input } from 'antd'
import { kindSpec, removeValues, restoreValues, capitalize, type ModeratedKind } from './contentModeration'

// This file is intentionally `.ts`, not `.tsx` — the one place it needs an
// element (the reason textarea inside the request-removal confirm dialog) it
// builds via React.createElement rather than JSX, so the file stays plain TS.

type ModalApi = ReturnType<typeof App.useApp>['modal']

/** Plain confirm (vouch / note / reply) — no reason to collect. */
function confirmPlain(
  modal: ModalApi,
  opts: { title: string; content?: React.ReactNode; okText: string; danger?: boolean },
): Promise<boolean> {
  return new Promise((resolve) => {
    modal.confirm({
      title: opts.title,
      content: opts.content,
      okText: opts.okText,
      okButtonProps: opts.danger ? { danger: true } : undefined,
      cancelText: 'Cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    })
  })
}

/**
 * Removing a request needs a reason (its own before-update guard requires a
 * non-empty removed_reason) — one modal.confirm whose content holds the
 * required Input.TextArea, mirroring how business_requests/page.tsx collects
 * its own removal reason. The reason lives in a plain ref (not React state)
 * because this content renders once, outside React's own tree; OK stays
 * disabled until modal.update() sees a non-empty trimmed value.
 */
function confirmRemoveWithReason(
  modal: ModalApi,
  opts: { title: string; body: string; defaultReason?: string },
): Promise<{ confirmed: boolean; reason: string }> {
  return new Promise((resolve) => {
    const reasonRef = { current: (opts.defaultReason ?? '').trim() }
    const instance = modal.confirm({
      title: opts.title,
      okText: 'Remove',
      okButtonProps: { danger: true, disabled: reasonRef.current === '' },
      cancelText: 'Cancel',
      content: React.createElement(
        'div',
        null,
        React.createElement('p', { style: { marginTop: 0 } }, opts.body),
        React.createElement(Input.TextArea, {
          rows: 3,
          defaultValue: opts.defaultReason,
          placeholder: 'Why is this request being removed?',
          'aria-label': 'Reason for removing this request',
          onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            reasonRef.current = e.target.value
            instance.update({ okButtonProps: { danger: true, disabled: reasonRef.current.trim() === '' } })
          },
        }),
      ),
      onOk: () => resolve({ confirmed: true, reason: reasonRef.current.trim() }),
      onCancel: () => resolve({ confirmed: false, reason: '' }),
    })
  })
}

export interface RemoveOptions {
  /** Pre-fills the request-removal reason textarea; ignored for every other kind. */
  reason?: string
  /**
   * Called once the removal has actually written to the database, with the
   * EXACT trimmed reason that was persisted (`undefined` for anything but a
   * request). Fix round 1, item 3: a caller that needs the real reason for a
   * request (ReportedContent's own report-stamping) can no longer get it
   * only by refetching the row afterward — a refetch that itself fails would
   * silently write a note with no reason. This callback hands the reason
   * back directly, from the same success path that just confirmed the write.
   */
  onRemoved?: (reason?: string) => void
}

/**
 * Remove/restore a single piece of reported Jaguar-Owned content — a vouch,
 * an owner note, an Ask reply, or an Ask request. Reused verbatim by Task P4.
 *
 * Every mutation passes `successNotification: false, errorNotification: false`
 * and shows its own `message` (global constraints) — Refine's own
 * notificationProvider would otherwise fire a second, generic toast.
 */
export function useContentModeration(kind: ModeratedKind) {
  const spec = kindSpec(kind)
  if (!spec) {
    // kind is typed ModeratedKind, so this can only happen if a caller
    // widens the type incorrectly — fail loudly rather than silently no-op.
    throw new Error(`useContentModeration: unknown kind "${kind}"`)
  }

  const { mutate } = useUpdate()
  const { modal, message } = App.useApp()
  const [isPending, setIsPending] = React.useState(false)
  // Fix round 1, item 3: a double-submit guard. Set the instant a caller
  // asks to remove/restore (BEFORE the confirm dialog even opens) and
  // cleared only once that whole operation settles (cancelled, or the
  // mutation resolved/rejected) — a second call in between opens no second
  // confirm and resolves false immediately. A plain ref, not state: it must
  // be readable synchronously on the very next call, with no re-render lag.
  const busyRef = React.useRef(false)

  const remove = React.useCallback(
    async (id: string, opts?: RemoveOptions): Promise<boolean> => {
      if (busyRef.current) return false
      busyRef.current = true
      try {
        let reason: string | undefined
        let confirmed: boolean
        if (spec.needsReason) {
          const result = await confirmRemoveWithReason(modal, {
            title: spec.removeTitle,
            body: spec.removeBody,
            defaultReason: opts?.reason,
          })
          confirmed = result.confirmed
          reason = result.reason
        } else {
          confirmed = await confirmPlain(modal, {
            title: spec.removeTitle,
            content: spec.removeBody,
            okText: 'Remove',
            danger: true,
          })
        }
        if (!confirmed) return false

        setIsPending(true)
        return await new Promise<boolean>((resolve) => {
          mutate(
            {
              resource: spec.resource,
              id,
              values: removeValues(kind, reason),
              successNotification: false,
              errorNotification: false,
            },
            {
              onSuccess: (response) => {
                setIsPending(false)
                // Fix round 1, item 9: RLS or a since-deleted row can make an
                // "update" affect zero rows and still come back as a
                // success — Supabase/PostgREST then returns no row data.
                // Treat that as a failure: no success toast, no onRemoved.
                if (!response?.data) {
                  message.error('That content no longer exists.')
                  resolve(false)
                  return
                }
                message.success(`${capitalize(spec.noun)} removed.`)
                // Fix round 2, item 1: a caller's onRemoved must never be
                // able to jam this promise. TanStack swallows a throw from a
                // per-call `mutate` onSuccess — it doesn't rethrow, doesn't
                // route to onError, and doesn't fail the mutation — so a
                // throw here would otherwise skip `resolve(true)` outright,
                // leaving `remove()`'s returned promise unsettled forever and
                // the double-submit guard (busyRef) stuck true permanently.
                try {
                  opts?.onRemoved?.(reason)
                } catch (e) {
                  // eslint-disable-next-line no-console
                  console.error('[useContentModeration] onRemoved failed', e)
                }
                resolve(true)
              },
              onError: (e) => {
                setIsPending(false)
                message.error(
                  (e as { message?: string } | undefined)?.message || `Failed to remove the ${spec.noun}.`,
                )
                resolve(false)
              },
            },
          )
        })
      } finally {
        busyRef.current = false
      }
    },
    [kind, spec, modal, message, mutate],
  )

  const restore = React.useCallback(
    async (id: string): Promise<boolean> => {
      if (busyRef.current) return false
      busyRef.current = true
      try {
        const confirmed = await confirmPlain(modal, {
          title: spec.restoreTitle,
          okText: 'Restore',
        })
        if (!confirmed) return false

        setIsPending(true)
        return await new Promise<boolean>((resolve) => {
          mutate(
            {
              resource: spec.resource,
              id,
              values: restoreValues(),
              successNotification: false,
              errorNotification: false,
            },
            {
              onSuccess: (response) => {
                setIsPending(false)
                if (!response?.data) {
                  message.error('That content no longer exists.')
                  resolve(false)
                  return
                }
                message.success(`${capitalize(spec.noun)} restored.`)
                resolve(true)
              },
              onError: (e) => {
                setIsPending(false)
                message.error(
                  (e as { message?: string } | undefined)?.message || `Failed to restore the ${spec.noun}.`,
                )
                resolve(false)
              },
            },
          )
        })
      } finally {
        busyRef.current = false
      }
    },
    [spec, modal, message, mutate],
  )

  return { remove, restore, isPending }
}
